export const APP_STORE_URL = 'https://onelink.to/uzr863';

export const DEEP_LINK_TIMEOUT_MS = 2500;

export const INVITER_DEEP_LINK_BASE = 'com.eloveit.tasktag:///';

/**
 * Builds the native deep link used after an email-invited user signs up on the
 * mobile web preview. When an inviter id is present it is appended as a query
 * param so the native app can open a 1:1 chat with the inviter post-login.
 */
export const buildInviterDeepLink = (inviterId?: string | null): string =>
  inviterId
    ? `${INVITER_DEEP_LINK_BASE}?inviterId=${encodeURIComponent(inviterId)}`
    : INVITER_DEEP_LINK_BASE;

/**
 * Extracts the `inviterId` query param from a deep link URL; '' if absent.
 * Deep links are user-controlled, so malformed percent-encoding (which makes
 * decodeURIComponent throw) is treated as absent rather than crashing the
 * deep-link handler.
 */
export const parseInviterIdFromUrl = (url?: string | null): string => {
  const match = url?.match(/[?&]inviterId=([^&]+)/);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return '';
  }
};

export const isMobileBrowser = (): boolean => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    window.navigator.userAgent
  );
};

/**
 * Attempts to open the native app via a deep link URL.
 * If the app does not open within 2.5 seconds, redirects to the App Store.
 *
 * Returns a cleanup function that should be called if the component unmounts
 * before the redirect fires (e.g. pass it as the return value of a useEffect).
 *
 * TODO (Option B): Replace the generic `com.eloveit.tasktag:///` deep link with
 * a backend-issued short-lived handoff token so the user lands authenticated in
 * the native app without having to log in again.
 */
export const detectAppInstallation = (
  deepLinkUrl: string,
  appStoreUrl: string
): (() => void) => {
  let hasAppOpened = false;
  let redirectTimer: number;
  const startTime = Date.now();

  const handleVisibilityChange = () => {
    if (document.hidden) {
      hasAppOpened = true;
      clearTimeout(redirectTimer);
    }
  };

  const handleBlur = () => {
    hasAppOpened = true;
    clearTimeout(redirectTimer);
  };

  const handleFocus = () => {
    const timeElapsed = Date.now() - startTime;
    if (timeElapsed < 1000 && !hasAppOpened) {
      setTimeout(() => {
        if (!hasAppOpened && window.location?.href) {
          window.location.href = appStoreUrl;
        }
      }, 1500);
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('blur', handleBlur);
  window.addEventListener('focus', handleFocus);

  if (window.location?.href) {
    window.location.href = deepLinkUrl;
  }

  redirectTimer = setTimeout(() => {
    if (!hasAppOpened) {
      window.location.href = appStoreUrl;
    }
  }, DEEP_LINK_TIMEOUT_MS);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('blur', handleBlur);
    window.removeEventListener('focus', handleFocus);
    clearTimeout(redirectTimer);
  };
};
